import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Button,
  Badge,
  Textarea,
  Separator,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  Input,
  Label,
} from '@rnd-payplus/alloy-react';
import {
  DatabaseZap,
  CreditCard,
  Building2,
  FolderSync,
  Upload,
  Webhook,
  ArrowRightLeft,
  Send,
  FileText,
  Receipt,
  Globe,
  Banknote,
  Plus,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
  Trash2,
} from 'lucide-react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  type Node,
  type Edge,
  type NodeProps,
  type Connection,
  type IsValidConnection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'sonner';
import { usePageShell } from '../contexts/PageShellContext';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchFlowDefinitions,
  fetchNodeTypes,
  createFlowDefinition,
  updateFlowDefinition,
  deleteFlowDefinition,
  setCurrentId,
  setDirty,
} from '../store/slices/flowDefinitionSlice';
import FlowNodeConfigPanel from '../components/FlowNodeConfigPanel';
import type { FlowNodeType } from '../services/flowDefinitionService';

// --- Icon map for dynamic rendering ---

const ICON_MAP: Record<string, React.ReactNode> = {
  DatabaseZap: <DatabaseZap className="size-4" />,
  CreditCard: <CreditCard className="size-4" />,
  Building2: <Building2 className="size-4" />,
  FolderSync: <FolderSync className="size-4" />,
  Upload: <Upload className="size-4" />,
  Webhook: <Webhook className="size-4" />,
  ArrowRightLeft: <ArrowRightLeft className="size-4" />,
  Send: <Send className="size-4" />,
  FileText: <FileText className="size-4" />,
  Receipt: <Receipt className="size-4" />,
  Globe: <Globe className="size-4" />,
  Banknote: <Banknote className="size-4" />,
};

// --- Node colors ---

const nodeColors: Record<string, { bg: string; border: string }> = {
  source: { bg: 'bg-blue-50', border: 'border-blue-300' },
  transform: { bg: 'bg-violet-50', border: 'border-violet-300' },
  destination: { bg: 'bg-green-50', border: 'border-green-300' },
};

// --- Custom React Flow node ---

function FlowNode({ data }: NodeProps) {
  const colors = nodeColors[data.category as string] || nodeColors.transform;
  const icon = data.icon ? ICON_MAP[data.icon as string] : null;
  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} px-2.5 py-2 min-w-[120px] max-w-[150px]`}>
      {data.category !== 'source' && (
        <Handle type="target" position={Position.Left} className="!bg-muted-foreground !w-2 !h-2" />
      )}
      <div className="flex items-center gap-1.5">
        {icon}
        <p className="text-sm font-medium leading-5">{data.label as string}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-4">{data.description as string}</p>
      {data.category !== 'destination' && (
        <Handle type="source" position={Position.Right} className="!bg-muted-foreground !w-2 !h-2" />
      )}
    </div>
  );
}

const nodeTypes = { flowNode: FlowNode };

// --- Section labels ---

const sectionLabels: Record<string, string> = {
  source: 'SOURCES',
  transform: 'TRANSFORMS',
  destination: 'DESTINATIONS',
};

// --- Test result types ---

interface TestNodeResult {
  nodeId: string;
  label: string;
  status: 'pending' | 'running' | 'complete' | 'error';
  output?: string;
  durationMs?: number;
}

// --- Edge style ---

const EDGE_STYLE = { stroke: '#e5e5e5', strokeWidth: 2 };

const SAMPLE_SAP_PAYLOAD = JSON.stringify(
  {
    header: {
      pymn_run_id: 'PR-2026-0414-001',
      pymn_run_date: '2026-04-14',
      company_code: '0001',
      vendor: 'V-100234',
      vendor_name: 'Acme Supplies Ltd',
      vendor_email: 'ap@acme.example',
      iban: 'GB29NWBK60161331926819',
      swift: 'NWBKGB2L',
      bank_key: '60-16-13',
      tot_pymt_vendor: 12540.75,
      ref_to_payment_doc: 'PAYDOC-778812',
    },
    payments: [
      {
        sap_acc_doc_num: '1900000123',
        ext_inv_ref_num: 'INV-2026-0088',
        fiscal_year: '2026',
        comp_code: '0001',
        doc_date: '2026-04-01',
        amount_gross: 12540.75,
        tax_amount: 2090.13,
        net_amount: 10450.62,
        currency: 'GBP',
        pymn_terms: 'NT30',
        gl_acc: '400000',
      },
    ],
    footer: { total_amount: 12540.75, payment_count: 1 },
  },
  null,
  2,
);

// --- Inner component (needs ReactFlowProvider) ---

function DataFlowsEditorInner() {
  const dispatch = useAppDispatch();
  const { definitions, nodeTypes: nodeTypesData, currentId, loading, dirty } = useAppSelector((s) => s.flowDefinition);
  const { getViewport, screenToFlowPosition } = useReactFlow();

  // Load data on mount
  useEffect(() => {
    dispatch(fetchFlowDefinitions());
    dispatch(fetchNodeTypes());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Current flow
  const currentDef = definitions.find((d) => d.id === currentId);

  const [nodes, setNodes, onNodesChange] = useNodesState(currentDef?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(currentDef?.edges || []);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [activeTab, setActiveTab] = useState('config');

  // Test state
  const [testInput, setTestInput] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState<TestNodeResult[] | null>(null);

  // New flow dialog
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newFlowName, setNewFlowName] = useState('');
  const [isNewFlow, setIsNewFlow] = useState(false);

  // Select first definition when loaded (skip if user clicked +)
  useEffect(() => {
    if (!currentId && !isNewFlow && definitions.length > 0) {
      dispatch(setCurrentId(definitions[0].id));
    }
  }, [definitions, currentId, isNewFlow, dispatch]);

  // Sync canvas when switching flows
  useEffect(() => {
    if (currentDef) {
      setNodes(currentDef.nodes as Node[]);
      setEdges(currentDef.edges as Edge[]);
      setSelectedNode(null);
      setTestResults(null);
    }
  }, [currentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark dirty on changes
  const handleNodesChange: typeof onNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      dispatch(setDirty(true));
    },
    [onNodesChange, dispatch],
  );

  const handleEdgesChange: typeof onEdgesChange = useCallback(
    (changes) => {
      onEdgesChange(changes);
      dispatch(setDirty(true));
    },
    [onEdgesChange, dispatch],
  );

  // Switch flow
  const switchFlow = useCallback(
    (id: string) => {
      setIsNewFlow(false);
      dispatch(setCurrentId(id));
      dispatch(setDirty(false));
    },
    [dispatch],
  );

  // Create new flow
  const createNewFlow = useCallback(() => {
    setIsNewFlow(true);
    dispatch(setCurrentId(null));
    setNodes([]);
    setEdges([]);
    setSelectedNode(null);
    setTestResults(null);
    dispatch(setDirty(false));
    setNewFlowName('');
    setShowNameDialog(true);
  }, [setNodes, setEdges, dispatch]);

  // Connect edges
  const onConnect = useCallback(
    (params: Connection) =>
      setEdges((eds) => {
        dispatch(setDirty(true));
        return addEdge({ ...params, style: EDGE_STYLE }, eds);
      }),
    [setEdges, dispatch],
  );

  // Validate connections: sources only output, destinations only input
  const isValidConnection: IsValidConnection = useCallback(
    (connection) => {
      if (connection.source === connection.target) return false;
      const sourceNode = nodes.find((n) => n.id === connection.source);
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (sourceNode?.data.category === 'destination') return false;
      if (targetNode?.data.category === 'source') return false;
      return true;
    },
    [nodes],
  );

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setActiveTab('config');
  }, []);

  const onPaneClick = useCallback(() => setSelectedNode(null), []);

  // Delete node
  const onDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNode(null);
      dispatch(setDirty(true));
    },
    [setNodes, setEdges, dispatch],
  );

  // Node config change
  const onConfigChange = useCallback(
    (nodeId: string, config: Record<string, unknown>) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, config } } : n)),
      );
      dispatch(setDirty(true));
      // Update selectedNode reference
      setSelectedNode((prev) => (prev?.id === nodeId ? { ...prev, data: { ...prev.data, config } } : prev));
    },
    [setNodes, dispatch],
  );

  // Drag and drop
  const onDragStart = (event: React.DragEvent, item: FlowNodeType) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(item));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const data = event.dataTransfer.getData('application/reactflow');
      if (!data) return;

      const item: FlowNodeType = JSON.parse(data);
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node = {
        id: `n-${Date.now()}`,
        type: 'flowNode',
        position,
        data: {
          label: item.label,
          description: item.description,
          category: item.category,
          nodeTypeId: item.id,
          icon: item.icon,
          config: {},
        },
      };

      setNodes((nds) => [...nds, newNode]);
      dispatch(setDirty(true));
    },
    [setNodes, dispatch, screenToFlowPosition],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Group node types by category
  const grouped = useMemo(() => {
    const groups: Record<string, FlowNodeType[]> = { source: [], transform: [], destination: [] };
    nodeTypesData.forEach((item) => {
      if (groups[item.category]) groups[item.category].push(item);
    });
    return groups;
  }, [nodeTypesData]);

  // Save
  const handleSave = useCallback(async () => {
    const viewport = getViewport();
    if (currentId && currentDef) {
      // Update existing
      await dispatch(
        updateFlowDefinition({
          id: currentId,
          data: { nodes, edges, viewport },
        }),
      ).unwrap();
      toast.success('Flow saved');
    } else {
      // Need a name for new flow
      setShowNameDialog(true);
    }
  }, [currentId, currentDef, nodes, edges, getViewport, dispatch]);

  // Keep a stable ref so the header button always calls the latest handleSave
  // (usePageShell only re-fires on updateKey changes, which can miss node/edge updates)
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;

  const handleCreateFlow = useCallback(async () => {
    if (!newFlowName.trim()) return;
    const viewport = getViewport();
    await dispatch(
      createFlowDefinition({
        name: newFlowName.trim(),
        nodes,
        edges,
        viewport,
      }),
    ).unwrap();
    setShowNameDialog(false);
    setNewFlowName('');
    setIsNewFlow(false);
    toast.success('Flow created');
  }, [newFlowName, nodes, edges, getViewport, dispatch]);

  // Delete flow
  const handleDeleteFlow = useCallback(async () => {
    if (!currentId) return;
    await dispatch(deleteFlowDefinition(currentId)).unwrap();
    toast.success('Flow deleted');
  }, [currentId, dispatch]);

  // Simulate test run
  const runTest = useCallback(async () => {
    if (!testInput.trim()) return;
    setTestRunning(true);

    const orderedNodes = nodes.map((n) => ({
      nodeId: n.id,
      label: n.data.label as string,
      status: 'pending' as const,
    }));

    setTestResults(orderedNodes.map((n) => ({ ...n })));

    for (let i = 0; i < orderedNodes.length; i++) {
      const currentNodeId = orderedNodes[i].nodeId;

      // Animate edges leaving the current node with blue highlight
      setEdges((eds) =>
        eds.map((e) =>
          e.source === currentNodeId
            ? { ...e, animated: true, style: { ...EDGE_STYLE, stroke: '#3b82f6', strokeWidth: 2.5 } }
            : { ...e, animated: false, style: EDGE_STYLE },
        ),
      );

      setTestResults((prev) =>
        prev?.map((r, idx) => (idx === i ? { ...r, status: 'running' } : r)) ?? null,
      );
      await new Promise((r) => setTimeout(r, 600 + Math.random() * 400));
      const success = Math.random() > 0.15;
      setTestResults((prev) =>
        prev?.map((r, idx) =>
          idx === i
            ? {
                ...r,
                status: success ? 'complete' : 'error',
                durationMs: Math.round(100 + Math.random() * 500),
                output: success
                  ? `Processed ${Math.round(Math.random() * 50 + 5)} records`
                  : 'Connection timeout',
              }
            : r,
        ) ?? null,
      );
      if (!success) break;
    }

    // Clear all edge animations and restore default style
    setEdges((eds) => eds.map((e) => ({ ...e, animated: false, style: EDGE_STYLE })));
    setTestRunning(false);
  }, [testInput, nodes, setEdges]);

  usePageShell({
    title: 'Data Flows',
    updateKey: `${currentId || ''}-${definitions.length}-${dirty}-${isNewFlow}`,
    primaryAction: (
      <Button onClick={() => saveRef.current()} className="relative">
        <Save className="mr-2 size-4" />
        Save
        {dirty && (
          <span className="absolute -top-1 -right-1 size-2.5 rounded-full bg-orange-500" />
        )}
      </Button>
    ),
    secondaryActions: (
      <>
        <Select value={currentId || ''} onValueChange={switchFlow}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={loading ? 'Loading...' : 'Select a flow'} />
          </SelectTrigger>
          <SelectContent>
            {definitions.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" className="size-9" onClick={createNewFlow}>
          <Plus className="size-4" />
        </Button>
        {currentId && (
          <Button variant="ghost" size="icon" className="size-9 text-muted-foreground hover:text-destructive" onClick={handleDeleteFlow}>
            <Trash2 className="size-4" />
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => {
            setActiveTab('test');
            if (testInput.trim()) runTest();
          }}
        >
          <Play className="mr-2 size-4" />
          Test Run
        </Button>
      </>
    ),
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'Data Flows' },
    ],
  });

  return (
    <div className="space-y-5">
      {/* 3-Panel Editor */}
      <div className="flex -mx-5 -mb-3 border-t" style={{ height: 'calc(100vh - 148px)' }}>
        {/* Left: Node Palette */}
        <div className="w-[224px] shrink-0 border-r overflow-y-auto p-3">
          {(['source', 'transform', 'destination'] as const).map((category) => (
            <div key={category} className="pb-4">
              <p className="text-[10px] font-semibold text-muted-foreground tracking-[0.5px] leading-4 mb-1">
                {sectionLabels[category]}
              </p>
              {(grouped[category] || []).map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-2 h-9 px-2 rounded-md cursor-grab hover:bg-accent"
                  draggable
                  onDragStart={(e) => onDragStart(e, item)}
                >
                  {item.icon && ICON_MAP[item.icon]}
                  <span className="text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Center: React Flow Canvas */}
        <div className="flex-1 min-w-0" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={nodeTypes}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls position="bottom-left" />
            <MiniMap
              position="bottom-right"
              nodeColor={(node) => {
                const cat = node.data?.category as string;
                if (cat === 'source') return '#93c5fd';
                if (cat === 'transform') return '#c4b5fd';
                if (cat === 'destination') return '#86efac';
                return '#d1d5db';
              }}
            />
          </ReactFlow>
        </div>

        {/* Right: Config/Test Panel */}
        <div className="w-[288px] shrink-0 border-l flex flex-col">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="w-full shrink-0 rounded-none border-b">
              <TabsTrigger className="flex-1" value="config">Config</TabsTrigger>
              <TabsTrigger className="flex-1" value="test">Test</TabsTrigger>
            </TabsList>

            <TabsContent value="config" className="flex-1 overflow-y-auto p-4 mt-0">
              {selectedNode ? (
                <FlowNodeConfigPanel
                  node={selectedNode}
                  onConfigChange={onConfigChange}
                  onDelete={onDeleteNode}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="size-8 rounded bg-muted" />
                  <p className="text-sm text-muted-foreground">Select a node to configure</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="test" className="flex-1 overflow-y-auto p-4 mt-0">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-medium">Sample Input (JSON)</label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => setTestInput(SAMPLE_SAP_PAYLOAD)}
                    >
                      Load sample
                    </Button>
                  </div>
                  <Textarea
                    rows={6}
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    placeholder={'{\n  "header": { "pymn_run_id": "...", ... },\n  "payments": [ ... ]\n}'}
                    className="font-mono text-xs"
                  />
                </div>
                <Button
                  className="w-full"
                  onClick={runTest}
                  disabled={testRunning || !testInput.trim()}
                >
                  {testRunning ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    <Play className="mr-2 size-4" />
                  )}
                  {testRunning ? 'Running...' : 'Run Test'}
                </Button>

                {testResults && (
                  <div className="space-y-2">
                    <Separator />
                    <p className="text-xs font-medium text-muted-foreground">Execution Results</p>
                    {testResults.map((result) => (
                      <div
                        key={result.nodeId}
                        className="flex items-start gap-2 rounded-md border p-2.5 text-sm"
                      >
                        {result.status === 'complete' && (
                          <CheckCircle2 className="size-4 mt-0.5 shrink-0 text-green-600" />
                        )}
                        {result.status === 'error' && (
                          <XCircle className="size-4 mt-0.5 shrink-0 text-red-600" />
                        )}
                        {result.status === 'running' && (
                          <Loader2 className="size-4 mt-0.5 shrink-0 text-blue-600 animate-spin" />
                        )}
                        {result.status === 'pending' && (
                          <div className="size-4 mt-0.5 shrink-0 rounded-full border-2 border-muted" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{result.label}</p>
                          {result.output && (
                            <p className="text-xs text-muted-foreground">{result.output}</p>
                          )}
                          {result.durationMs !== undefined && (
                            <p className="text-xs text-muted-foreground">{result.durationMs}ms</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!testResults && !testRunning && (
                  <div className="flex flex-col items-center justify-center py-8 gap-2">
                    <Play className="size-8 text-muted-foreground/30" />
                    <p className="text-sm text-muted-foreground text-center">
                      Paste sample data and click Run Test to see how data flows through each node
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* New Flow Name Dialog */}
      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name Your Flow</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="flow-name">Flow Name</Label>
            <Input
              id="flow-name"
              className="mt-2"
              placeholder="e.g. SAP Invoice Processing"
              value={newFlowName}
              onChange={(e) => setNewFlowName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFlow()}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleCreateFlow} disabled={!newFlowName.trim()}>
              Create Flow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Main component wrapped with ReactFlowProvider ---

const DataFlowsPageComplete = () => (
  <ReactFlowProvider>
    <DataFlowsEditorInner />
  </ReactFlowProvider>
);

export default DataFlowsPageComplete;
